import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { formatDuration, localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import {
	BusyIndicator,
	DateSlider,
	DateString,
	PersonAvatar,
	PipeSeparator,
} from "client/Components";
import {
	MyPlanningLiveInformationResponseModel,
	NedapOnsResultStatus,
	PlannedVisitListItem
} from "client/Api/Workspace/LiveInformation/NedapOns/nedapOnsLiveInformation";
import {
	InterestedNedapOnsParty,
	NedapOnsLiveInformationProvider
} from "client/Features/Workspace/Tiles/NedapOns/provider";
import { LiveTileContent } from "../LiveTileContent";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { DimensionToRender } from "../../RenderState/common";
import styles from "./NedapOnsMyPlanning.module.less";
import { ListItem } from "../ListItem";
import { DestinationWindow } from "client/Api/Apps/common";
import { formatAddress, prefixNameWithGender } from "./misc";
import { AlertManager, useAlerts } from "client/Hooks";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { navigateToNedapOnsApp } from "client_legacy/Apps/appNavigation";
import { TimeZone } from "client/DateAndTime/timezones";
import { formatForDto, getStartOfDay } from "client/DateAndTime/dateTimeUtils";

export type NedapOnsMyPlanningProps = {
	tabId: Guid;
	connectionId: number;
	provider: NedapOnsLiveInformationProvider;
	destinationWindow: DestinationWindow;
	dimensionToRender: DimensionToRender;
	environmentAppId: number;
	timezone: TimeZone;
	addAnalyticsEvent(): void;
};

export const NedapOnsMyPlanning = (props: NedapOnsMyPlanningProps): ReactElement => {
	const {
		tabId,
		connectionId,
		provider,
		dimensionToRender,
		destinationWindow,
		environmentAppId,
		timezone,
		addAnalyticsEvent
	} = props;

	const [activeDate, setActiveDate] = useState<Date>(
		(): Date => getStartOfDay(
			new Date(),
			timezone
		)
	);

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const isTodayButtonAvailable: boolean = dimensionToRender.dimension.columnSpan === 8;
	const isWideView: boolean = dimensionToRender.dimension.columnSpan >= 6;

	useEffect(
		(): (() => void) => {
			setLoadingState((x) => ({
				...x,
				isLoading: true
			}));

			const party: InterestedNedapOnsParty = {
				partyId: tabId,
				resourceId: {
					connectionId,
					date: formatForDto(
						getStartOfDay(
							activeDate,
							timezone
						),
						timezone
					)
				},
				informAboutGlobalRetrievalFailure: (): void => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true
					});
				},
				informAboutConsecutiveErrors: (): void => {
					setLoadingState({
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					});
				},
				updateMyPlanning: (response: MyPlanningLiveInformationResponseModel): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: false,
						response
					}));
				}
			};

			provider.register(party, { myPlanning: true });

			return (): void => {
				provider.unregister(party);
			};
		},
		[
			connectionId,
			provider,
			activeDate,
			tabId,
			timezone
		]
	);

	const navigationHandler = useCallback(
		(): void => {
			addAnalyticsEvent();

			navigateToNedapOnsApp({
				id: environmentAppId,
				destinationWindow: destinationWindow,
				view: "planning"
			});
		},
		[
			addAnalyticsEvent,
			destinationWindow,
			environmentAppId
		]
	);

	const tileRefreshHandler = (): void => {
		setLoadingState((x) => ({
			...x,
			isLoading: true
		}));
		provider.forceReload();
	};

	const renderContent = (): ReactElement => {
		if (loadingState.isLoading) {
			return <BusyIndicator />;
		}

		if (loadingState.hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={tileRefreshHandler} />;
		}

		if (loadingState.hasGlobalFailure) {
			return (
				<LiveTileContent.NoResults
					trackingId="has-error"
					icon="gear"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeWereUnableToRetrieveTheItems")}
					onRetry={tileRefreshHandler}
				/>
			);
		}

		const { resultStatus } = loadingState.response;

		if (resultStatus === NedapOnsResultStatus.NoAccess) {
			return (
				<LiveTileContent.Message
					text={localize("_NoAccess")}
				/>
			);
		}

		if (resultStatus === NedapOnsResultStatus.GeneralFailure) {
			return (
				<LiveTileContent.Message
					text={localize("_UnableToRetrieveItems")}
				/>
			);
		}

		if (
			loadingState.response
				.items
				.length === 0
		) {
			return (
				<LiveTileContent.Message
					text={localize("_ThereIsNoPlanningForThisDay")}
				/>
			);
		}

		return (
			<LiveTileContent.Scrollable>
				<LiveTileContent.List>
					{loadingState.response?.items
						.map((x: PlannedVisitListItem) => (
							<MyPlanningListItem
								key={x.calculatedStart}
								item={x}
								isWideView={isWideView}
								destinationWindow={destinationWindow}
								timezone={timezone}
								onClick={addAnalyticsEvent}
							/>
						))
					}
				</LiveTileContent.List>
			</LiveTileContent.Scrollable>
		);
	};

	return (
		<>
			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					<div className={styles.sliderContainer}>
						<DateSlider
							value={activeDate}
							onChange={setActiveDate}
							isTodayButtonAvailable={isTodayButtonAvailable}
							trackingId="nedap-ons-my-planning-week-view"
							timezone={timezone}
						/>
					</div>
					{renderContent()}
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={localize("_MyPlanning")}
				onClick={navigationHandler}
			/>
		</>
	);
};
NedapOnsMyPlanning.displayName = "NedapOnsMyPlanning";

type MyPlanningListItemProps = {
	isWideView: boolean;
	item: PlannedVisitListItem;
	destinationWindow: DestinationWindow;
	timezone: TimeZone;
	onClick(): void;
};

export const MyPlanningListItem = (props: MyPlanningListItemProps): ReactElement => {
	const {
		item,
		isWideView,
		destinationWindow,
		timezone,
		onClick
	} = props;

	const alertManager: AlertManager = useAlerts();

	const listItemClickHandler = useCallback(
		(): void => {
			onClick();

			if (item.clientOverviewUrl) {
				navigateTo(
					item.clientOverviewUrl,
					destinationWindow === DestinationWindow.NewWindow
				);
			} else {
				alertManager.showInfo(localize("_TheClientIsNotLinkedToOnsAdministration"));
			}
		},
		[
			alertManager,
			destinationWindow,
			item.clientOverviewUrl,
			onClick
		]
	);

	return (
		<ListItem
			isHoverable
			onClick={listItemClickHandler}
		>
			<div className={styles.timeSection}>
				<ListItem.Title>
					<DateString
						value={item.calculatedStart}
						format="T"
						timezone={timezone}
					/>
				</ListItem.Title>
				<ListItem.Info>
					{formatDuration(item.durationInSeconds)}
				</ListItem.Info>
			</div>
			{isWideView && (
				<ListItem.Icon>
					<PersonAvatar
						initials={item.clientInitials}
						size="small"
					/>
				</ListItem.Icon>
			)}
			<ListItem.Content>
				<ListItem.Row>
					<ListItem.Title>
						{prefixNameWithGender(item.clientName, item.clientGender)}
					</ListItem.Title>
				</ListItem.Row>

				<ListItem.Row>
					<ListItem.Info>
						{formatAddress(item.address, isWideView)}
						{isWideView && item.clientIdentificationCode && (
							<>
								{item.address && (
									<PipeSeparator />
								)}
								{item.clientIdentificationCode}
							</>
						)}
					</ListItem.Info>
				</ListItem.Row>
			</ListItem.Content>
		</ListItem>
	);
};
MyPlanningListItem.displayName = "MyPlanningListItem";

type LoadingState = {
	isLoading: true;
} | {
	isLoading: false;
} & (
		{
			hasTooManyConsecutiveErrors: true;
			hasGlobalFailure?: undefined;
		}
		|
		{
			hasGlobalFailure: true;
			hasTooManyConsecutiveErrors?: undefined;
		}
		|
		{
			hasTooManyConsecutiveErrors: false;
			hasGlobalFailure: false;
			response: MyPlanningLiveInformationResponseModel;
		}
	);